import React, { useState, useEffect } from 'react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import Select from '../UI/Select';
import { api } from '../../services/api';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchProjectById } from '../../store/slices/projectsSlice';
import { Goal } from '../Projects/Goals';
import { Milestone } from '../Projects/Milestones';

interface VotingSessionSchedulerProps {
  projectId: string;
  onSessionCreated?: () => void;
  onCancel?: () => void;
}

interface ProjectFile {
  id?: string;
  _id?: string;
  originalFilename: string;
  type: string;
}

const VotingSessionScheduler: React.FC<VotingSessionSchedulerProps> = ({
  projectId,
  onSessionCreated,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const { accessToken } = useAppSelector((state) => state.auth);
  const { items: projectItems } = useAppSelector((state) => state.projects);
  const { items: goalItems, projectGoals } = useAppSelector((state) => state.goals);
  const { items: milestoneItems, projectMilestones } = useAppSelector((state) => state.milestones);

  const project = projectItems[projectId] || Object.values(projectItems).find((p: any) => (p._id || p.id) === projectId);
  const projectGoalsList = projectGoals[projectId]?.map((id) => goalItems[id]).filter(Boolean) as Goal[] || [];
  const projectMilestonesList = projectMilestones[projectId]?.map((id) => milestoneItems[id]).filter(Boolean) as Milestone[] || [];
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);

  // Fetch project files
  useEffect(() => {
    const fetchFiles = async () => {
      if (!projectId || !accessToken) return;
      try {
        const files = await api.projectFiles.getProjectFiles(projectId, false, accessToken);
        setProjectFiles(files.filter((f: any) => f.type === 'document' && !f.deletedAt) as ProjectFile[]);
      } catch (error) {
        console.error('Error fetching project files:', error);
      }
    };
    fetchFiles();
  }, [projectId, accessToken]);

  const [formData, setFormData] = useState({
    sessionNumber: 1,
    type: 'open' as 'open' | 'anonymous',
    question: '',
    startDate: '',
    endDate: '',
    duration: 1, // days
    geoRestricted: false,
    allowedParishes: [] as string[],
    connectedGoals: [] as string[],
    connectedMilestones: [] as string[],
    connectedDocuments: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate end date when start date or duration changes
  useEffect(() => {
    if (formData.startDate) {
      const start = new Date(formData.startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + formData.duration);
      setFormData((prev) => ({
        ...prev,
        endDate: end.toISOString().split('T')[0] + 'T' + end.toTimeString().slice(0, 5),
      }));
    }
  }, [formData.startDate, formData.duration]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) {
      setError('Authentication required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const sessionData = {
        sessionNumber: formData.sessionNumber,
        type: formData.type,
        question: formData.question,
        options: [
          { id: 'yes', label: 'Yes' },
          { id: 'no', label: 'No' },
          { id: 'abstain', label: 'Abstain' },
        ],
        startDate: new Date(formData.startDate).toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        geoRestricted: formData.geoRestricted,
        allowedParishes: formData.allowedParishes,
        connectedGoals: formData.connectedGoals,
        connectedMilestones: formData.connectedMilestones,
        connectedDocuments: formData.connectedDocuments,
      };

      await api.voting.createSession(projectId, sessionData, accessToken);
      
      // Refresh project data
      await dispatch(fetchProjectById(projectId));
      
      if (onSessionCreated) {
        onSessionCreated();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create voting session');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-lg text-[var(--color-error-700)] text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Session Number"
          type="number"
          min={1}
          max={3}
          value={formData.sessionNumber}
          onChange={(e) => setFormData((prev) => ({ ...prev, sessionNumber: parseInt(e.target.value) }))}
          required
        />
        <Select
          label="Voting Type"
          value={formData.type}
          onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value as 'open' | 'anonymous' }))}
          options={[
            { value: 'open', label: 'Open (Registered Users)' },
            { value: 'anonymous', label: 'Anonymous' },
          ]}
          required
        />
      </div>

      <Input
        label="Question"
        value={formData.question}
        onChange={(e) => setFormData((prev) => ({ ...prev, question: e.target.value }))}
        placeholder="What should citizens vote on?"
        required
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Start Date & Time"
          type="datetime-local"
          value={formData.startDate}
          onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
          required
        />
        <div>
          <Input
            label="Duration (days)"
            type="number"
            min={1}
            value={formData.duration}
            onChange={(e) => setFormData((prev) => ({ ...prev, duration: parseInt(e.target.value) || 1 }))}
            required
          />
          {formData.endDate && (
            <p className="text-xs text-[var(--color-subtext-color)] mt-1">
              Ends: {new Date(formData.endDate).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* Connections */}
      <div className="space-y-3">
        <h4 className="font-medium text-sm text-[var(--color-default-font)]">Connect to (Optional)</h4>
        
        {projectGoalsList.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">Goals</label>
            <select
              multiple
              className="w-full border border-[var(--color-neutral-border)] rounded-lg p-2 text-sm bg-[var(--color-default-background)] text-[var(--color-default-font)]"
              value={formData.connectedGoals}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                setFormData((prev) => ({ ...prev, connectedGoals: selected }));
              }}
            >
              {projectGoalsList.map((goal) => (
                <option key={goal._id || goal.id} value={goal._id || goal.id}>
                  {goal.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {projectMilestonesList.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">Milestones</label>
            <select
              multiple
              className="w-full border border-[var(--color-neutral-border)] rounded-lg p-2 text-sm bg-[var(--color-default-background)] text-[var(--color-default-font)]"
              value={formData.connectedMilestones}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                setFormData((prev) => ({ ...prev, connectedMilestones: selected }));
              }}
            >
              {projectMilestonesList.map((milestone) => (
                <option key={milestone._id || milestone.id} value={milestone._id || milestone.id}>
                  {milestone.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {projectFiles.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">Documents</label>
            <select
              multiple
              className="w-full border border-[var(--color-neutral-border)] rounded-lg p-2 text-sm bg-[var(--color-default-background)] text-[var(--color-default-font)]"
              value={formData.connectedDocuments}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                setFormData((prev) => ({ ...prev, connectedDocuments: selected }));
              }}
            >
              {projectFiles
                .filter((file: ProjectFile) => file.type === 'document')
                .map((file: ProjectFile) => (
                  <option key={file.id || file._id} value={file.id || file._id}>
                    {file.originalFilename}
                  </option>
                ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" isLoading={isSubmitting}>
          Schedule Session
        </Button>
      </div>
    </form>
  );
};

export default VotingSessionScheduler;

